import { FC, useCallback, useEffect, useState } from "react";

import { serverGetDatadogEmbedUrl } from "api/api-server-data";
import { t } from "i18n/i18n";

type EmbedState =
  | { kind: "initial" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; url: string }
  | { kind: "unavailable" };

type DatadogEmbeddedDashboardProps = {
  sectionId: string;
};

export const DatadogEmbeddedDashboard: FC<DatadogEmbeddedDashboardProps> = ({ sectionId }) => {
  const [state, setState] = useState<EmbedState>({ kind: "initial" });

  const fetchDashboardEmbedUrl = useCallback(async () => {
    setState({ kind: "loading" });

    try {
      const response = await serverGetDatadogEmbedUrl(sectionId);
      if (!response) {
        setState({ kind: "unavailable" });
        return;
      }

      setState({ kind: "ready", url: response.iframe_url });
    } catch (error) {
      setState({ kind: "error", message: error.message });
    }
  }, [sectionId]);

  useEffect(() => {
    if (state.kind === "initial") {
      fetchDashboardEmbedUrl();
    }
  }, [fetchDashboardEmbedUrl, state]);

  if (state.kind === "loading") {
    return (
      <div className="flex justify-center border">
        <div className="text-gray-60 text-lg">{t("common.loading_ellipsis")}</div>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div className="border">
        <div className="text-lg text-red">Failed to load Datadog dashboard</div>
        <p className="text-sm text-red">{state.message}</p>
      </div>
    );
  }

  if (state.kind === "unavailable") {
    return (
      <div className="border">
        <div className="text-lg text-red">Datadog dashboard is unavailable</div>
      </div>
    );
  }

  if (state.kind === "ready") {
    return (
      <iframe
        allow="fullscreen"
        className="min-h-[70vh] w-full border"
        loading="lazy"
        referrerPolicy="no-referrer"
        src={state.url}
        title="Datadog Dashboard"
      />
    );
  }

  return null;
};
